import React from 'react';
import type { ECharts } from 'echarts/core';
import { useTheme } from '@mui/material';
import { Chart, DownloadButton, EmptyState, Panel, SegmentedControl, type InfoHintProps } from '../../../../ui-kit';
import { downloadChartImage, type ChartImageFormat } from '../../../../utils/chartDownload';
import type { ReportPayload } from '../../../../types/reportDashboard';
import { useReportCohort, type ReportQuery } from '../useReportCohort';

export interface ReportChartSeries {
  key: string;
  label: string;
  values: (number | null)[];
}

/**
 * The premium on the same cases, drawn with the count rather than instead of
 * it. Counts and dollars cannot share an axis, so the chart is drawn in cases
 * and each premium is printed under its count and named on hover. One entry
 * per drawn line or bar, in the same order.
 */
export interface ReportChartAlongside {
  series: (source: ReportPayload) => ReportChartSeries[];
  formatValue: (value: number) => string;
}

interface ReportChartPanelProps {
  title: string;
  subtitle?: React.ReactNode;
  info?: React.ReactNode;
  infoTone?: InfoHintProps['tone'];
  /** Controls after the Cohort select, e.g. the metric select. */
  actions?: React.ReactNode;
  payload: ReportPayload;
  query: ReportQuery;
  /** The lines, read from the report at this chart's cohort grouping. */
  series: (source: ReportPayload) => ReportChartSeries[];
  formatValue: (value: number) => string;
  /** Names the y-axis, as her chart does: "Count of Policies". */
  axisLabel?: string;
  /** The premium on the same cases, printed under each count. */
  alongside?: ReportChartAlongside;
  /**
   * With `alongside`, a Cases / Premium switch that changes which of the two is
   * drawn. Both figures stay printed on every point either way; only the line
   * changes, since counts and dollars cannot share an axis.
   */
  swappable?: boolean;
  /**
   * `line` (the default) for measures followed over time; `stacked` for the
   * parts of one whole each cohort, e.g. open cases by what they wait on.
   */
  kind?: 'line' | 'stacked';
  loading?: boolean;
}

/**
 * One of the report's cohort charts, in its panel, with the Cohort select,
 * Download and full-screen view every board chart has.
 *
 * Lines over the months, each point carrying its value the way her printed
 * charts do. More than one line gets a legend. Colours come from the chart
 * theme through the kit's `Chart`.
 */
export default function ReportChartPanel({
  title, subtitle, info, infoTone, actions, payload, query, series: pickCount,
  formatValue: formatCount, axisLabel: countAxis, alongside, swappable = false, kind = 'line', loading,
}: ReportChartPanelProps) {
  const theme = useTheme();
  const cohort = useReportCohort(payload, query);
  const [measure, setMeasure] = React.useState<'count' | 'premium'>('count');
  // Drawing the premium: the two swap places, so the line is the premium and
  // the count is the figure printed under it.
  const drawPremium = swappable && measure === 'premium' && Boolean(alongside);
  const pick = drawPremium && alongside ? alongside.series : pickCount;
  const formatValue = drawPremium && alongside ? alongside.formatValue : formatCount;
  const axisLabel = drawPremium ? undefined : countAxis;
  const categories = React.useMemo(() => cohort.source.periods.map((p) => p.label), [cohort.source]);
  const series = React.useMemo(() => pick(cohort.source), [pick, cohort.source]);
  const beside = React.useMemo(() => {
    if (!alongside) return null;
    return drawPremium ? pickCount(cohort.source) : alongside.series(cohort.source);
  }, [alongside, drawPremium, pickCount, cohort.source]);
  const besideFormat = drawPremium ? formatCount : alongside?.formatValue;
  // The chart in the panel, not the expanded copy, is the one saved.
  const chartRef = React.useRef<ECharts | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const hasData = series.some((s) => s.values.some((v) => v !== null));

  const download = (format: ChartImageFormat) => {
    setProblem(null);
    try {
      downloadChartImage(chartRef.current, format, title);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'The chart could not be downloaded.');
    }
  };

  const option = React.useMemo(() => {
    const many = series.length > 1;
    const fewPoints = categories.length <= 12;

    // Two lines close together print their values on top of each other. At
    // each point the higher line keeps its value above and the others go
    // below, so the pair reads apart; anything still touching is hidden and
    // hovering reads it.
    // Its premium at this point, already written out, or nothing where the
    // chart has no premium to show.
    const besideAt = (si: number, i: number) => {
      const v = beside?.[si]?.values[i];
      return v === null || v === undefined || !besideFormat ? null : besideFormat(v);
    };
    const besideTotal = (i: number) => {
      if (!beside || !besideFormat) return null;
      const sum = beside.reduce((n, o) => n + (o.values[i] ?? 0), 0);
      return sum ? besideFormat(sum) : null;
    };

    const peak = Math.max(0, ...series.flatMap((o) => o.values.filter((v): v is number => v !== null)));
    // The line with the highest value at each point.
    const higher = categories.map((_, i) => {
      let at = -1;
      series.forEach((o, oi) => {
        const v = o.values[i];
        if (v !== null && (at < 0 || v > (series[at].values[i] ?? -Infinity))) at = oi;
      });
      return at;
    });
    const lineSeries = () => series.map((s, si) => ({
      name: s.label,
      type: 'line',
      // ECharts' own marker for "no value here".
      data: s.values.map((v, i) => {
        if (v === null) return '-';
        if (!many) return v;
        if (higher[i] === si) return { value: v, label: { position: 'top' } };
        // The lower line's value goes into the gap above it, where the space
        // between the two lines is clear — under the point it would print over
        // the month names. With the lines close together there is no gap, so it
        // goes under, or beside when that would land on the axis.
        const gap = (series[higher[i]]?.values[i] ?? v) - v;
        const position = gap > peak * 0.18 ? 'top' : v < peak * 0.12 ? 'right' : 'bottom';
        return { value: v, label: { position } };
      }),
      symbol: 'circle',
      symbolSize: 7,
      // Values on the points only while there are few enough to read, each
      // with its premium on a second line.
      label: {
        show: fewPoints,
        position: 'top',
        lineHeight: 15,
        formatter: (p: { value: number | string; dataIndex: number }) => {
          if (typeof p.value !== 'number') return '';
          const money = besideAt(si, p.dataIndex);
          return money ? `${formatValue(p.value)}\n${money}` : formatValue(p.value);
        },
      },
      labelLayout: { hideOverlap: true },
    }));

    // One bar per cohort, its parts stacked. A thin line of the panel's own
    // colour between parts, and only the top of the bar rounded. The total
    // is printed above the bar; each part's value is on hover.
    const stackedSeries = () => series.map((s, si) => {
      const top = si === series.length - 1;
      return {
        name: s.label,
        type: 'bar',
        stack: 'total',
        data: s.values.map((v) => (v === null ? 0 : v)),
        barMaxWidth: 40,
        itemStyle: {
          borderColor: theme.tokens.surface.raised,
          borderWidth: 1,
          borderRadius: top ? [4, 4, 0, 0] : 0,
        },
        label: top ? {
          show: fewPoints,
          position: 'top',
          color: theme.tokens.text.secondary,
          lineHeight: 15,
          formatter: (p: { dataIndex: number }) => {
            const cases = formatValue(series.reduce((n, o) => n + (o.values[p.dataIndex] ?? 0), 0));
            const money = besideTotal(p.dataIndex);
            return money ? `${cases}\n${money}` : cases;
          },
        } : { show: false },
      };
    });

    return {
      // Room for the labels: a label carrying its premium is two lines tall,
      // above the highest point as well as below the lowest.
      grid: {
        left: axisLabel ? 28 : 8,
        right: 24,
        top: (many ? 44 : 24) + (beside && kind === 'line' ? 20 : 0),
        bottom: beside && kind === 'line' ? 16 : 8,
        containLabel: true,
      },
      legend: many ? { top: 0, left: 0 } : { show: false },
      tooltip: {
        trigger: 'axis',
        // "Applications  791 · $2.21M" — the count and the premium on the same
        // line, so neither has to be looked up separately.
        formatter: (params: Array<{ axisValueLabel?: string; marker?: string; seriesIndex: number; dataIndex: number; value: number | string }>) => {
          const rows = params.map((p) => {
            const count = typeof p.value === 'number' ? formatValue(p.value) : '—';
            const money = besideAt(p.seriesIndex, p.dataIndex);
            const name = series[p.seriesIndex]?.label ?? '';
            return `${p.marker ?? ''} ${name}&nbsp;&nbsp;<b>${count}</b>${money ? ` · <b>${money}</b>` : ''}`;
          });
          return [params[0]?.axisValueLabel ?? '', ...rows].join('<br/>');
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisTick: { show: false },
        // A cut-short cohort's note goes on a second line: "Sep 26" over "(1–18)".
        axisLabel: { interval: 0, hideOverlap: true, formatter: (label: string) => label.replace(' (', '\n(') },
      },
      yAxis: {
        type: 'value',
        name: axisLabel,
        nameLocation: 'middle',
        nameGap: 40,
        axisLabel: { formatter: (v: number) => formatValue(v) },
      },
      series: kind === 'stacked' ? stackedSeries() : lineSeries(),
    };
  }, [categories, series, beside, besideFormat, formatValue, axisLabel, kind, theme]);

  const chart = (fill: boolean) => (hasData ? (
    <Chart
      option={option}
      height={fill ? undefined : beside ? 360 : 300}
      fill={fill}
      onReady={fill ? undefined : (instance) => { chartRef.current = instance; }}
      ariaLabel={`${title}, ${categories[0] ?? ''} to ${categories[categories.length - 1] ?? ''}`}
    />
  ) : (
    <EmptyState message="No data for these cohorts." height={fill ? undefined : beside ? 360 : 300} />
  ));

  return (
    <Panel
      title={title}
      subtitle={subtitle}
      info={info}
      infoTone={infoTone}
      tools={
        <>
          {cohort.control}
          {swappable && alongside && (
            <SegmentedControl
              ariaLabel="Trend by"
              value={measure}
              onChange={setMeasure}
              options={[{ value: 'count', label: 'Cases' }, { value: 'premium', label: 'Premium' }]}
            />
          )}
          {actions}
        </>
      }
      actions={
        <>
          <DownloadButton
            formats={[
              { key: 'png', label: 'Image (.png)', onDownload: () => download('png') },
              { key: 'svg', label: 'Vector (.svg)', description: 'Sharp at any size', onDownload: () => download('svg') },
            ]}
            disabled={loading || !hasData}
          />
        </>
      }
      footer={problem}
      loading={loading || cohort.regrouping}
      loadingHeight={beside ? 360 : 300}
      expanded={hasData ? { content: chart(true) } : undefined}
    >
      {chart(false)}
    </Panel>
  );
}
